import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { ConfiguratorProductPrice } from "../../../common/ConfiguratorProductPrice";
import { Flair } from "../../../common/Flair";
import { Image } from "../../../common/Image";
import { Link } from "../../../common/Link";
import RatingGraphic from "../../../common/RatingGraphic";
import { RichText } from "../../../common/RichText";
import { DropdownHeadersType } from "../../../templates/ShopTileTemplate";
import { DiscountDisplayMode } from "../../configurator/constants";
import { Dropdown } from "../Dropdown";
import { CompareTileType } from "../ShopCompareTypes";
import { ATTRIBUTE_CLASSES } from "../ShopCompareTypes";
import { AttributeList } from "../elements/AttributeList";

import styles from "./index.module.scss";

interface ICompareTileProps {
    tile: CompareTileType;
    dropdownHeaders: DropdownHeadersType;
    handleSelectProduct?: (oldTile: number, newTile: number) => void;
    selectedProducts?: number[];
}

export const CompareTile = (props: ICompareTileProps) => {
    const tile = props.tile.compare_tile;
    if (!tile) {
        return null;
    }
    const attributeList = tile.feel_options.map((feelOption) => ({
        title: feelOption,
        class:
            //eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            (
                Object.keys(ATTRIBUTE_CLASSES) as Array<
                    keyof typeof ATTRIBUTE_CLASSES
                >
            ).find((key) => ATTRIBUTE_CLASSES[key] === feelOption) || "",
    }));
    return (
        <div className={styles.container} key={JSON.stringify(tile.id)}>
            {tile.primary_flair && (
                <div className={styles.primaryFlair}>
                    <Flair flair={tile.primary_flair} />
                </div>
            )}
            <div className={styles.contentContainer}>
                {tile.product_image?.url && (
                    <Image
                        alt={tile.product_image.title || ""}
                        src={tile.product_image.url}
                    />
                )}
                {tile.secondary_flair && (
                    <div className={styles.secondaryFlair}>
                        <Flair flair={tile.secondary_flair} />
                    </div>
                )}
                <Dropdown
                    dropdownHeaders={props.dropdownHeaders}
                    selectedProduct={tile}
                    handleSelectProduct={props.handleSelectProduct}
                    selectedProducts={props.selectedProducts}
                />
                <a
                    href={`${tile.product}#reviews_body`}
                    className={styles.starRating}
                >
                    <RatingGraphic
                        cardClass={styles.starRating}
                        cardSize="large"
                        numReviews={tile.num_reviews}
                        rating={tile.rating}
                        reviewNumberClass={styles.reviewLink}
                    />
                </a>
                {tile.description && (
                    <RichText
                        html={tile.description}
                        className={styles.description}
                    />
                )}
                <AttributeList list={attributeList} />
            </div>
            <div className={styles.priceContainer}>
                <div>
                    <ConfiguratorProductPrice
                        price={tile.price}
                        strikeThroughMSRP={false}
                        discountDisplayMode={DiscountDisplayMode.SHOW_ABSOLUTE}
                        actualPriceStyle={"shop"}
                    ></ConfiguratorProductPrice>
                    {tile.price.per_month_term_length && (
                        <div className={styles.financingPlanContainer}>
                            <span>
                                or {tile.price.per_month} for{" "}
                                <span>
                                    {tile.price.per_month_term_length.toString()}{" "}
                                </span>
                            </span>{" "}
                            <br />
                            <span>
                                (total payments of ${tile.price.retail})
                            </span>
                        </div>
                    )}
                </div>
                <div className={styles.shopNowButton}>
                    <Link
                        href={tile.link}
                        className="button al-comparisongrid-shopnow"
                    >
                        {t`Shop Now`}
                        <span className="ada-screenreader-only">
                            {interpolate(t`for %(productName)s`, {
                                productName: tile.title || "",
                            })}
                        </span>
                    </Link>
                </div>
            </div>
        </div>
    );
};
